<script setup lang="ts">
  import type {
    ChangeStakeholderBillingDetailsRequestPayload,
    Client
  } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Checkbox,
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupNumber,
    RadioCardGroup,
    RadioCardGroupItem,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { IconCopy, IconEdit } from '@gat-ship/icons'
  import { text } from '@gat-ship/stakeholders'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import type { ShowStakeholderUpdateBillingDetailsParams } from '../providers/stakeholder-modals'
  import { CurrencyPicker } from '@gat-ship/ref-data'
  import { billingText } from '@gat-ship/stakeholders'

  // Edit-modal setup (open model + injected service + staged stakeholder +
  // title/description refs) is the same idiomatic boilerplate in every modal.
  // fallow-ignore-next-line code-duplication
  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useStakeholderContext()

  const _stakeholder = ref<Client>()
  const modalTitle = ref('')
  const modalDescription = ref('')

  const schema = z
    .object({
      stakeholderId: z.string(),
      invoiceEmail: z.string().optional(),
      useDefaultBillingCurrency: z.enum(['default', 'custom']),
      billingCurrencyCode: z.string().optional(),
      useDefaultPaymentTerms: z.enum(['default', 'custom']),
      paymentTermsPeriodInDays: z.number().optional(),
      paymentTermsUseRunningMonth: z.boolean().optional()
    })
    .superRefine((data, ctx) => {
      if (
        data.useDefaultBillingCurrency === 'custom' &&
        (!data.billingCurrencyCode || data.billingCurrencyCode.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billingCurrencyCode'],
          message: 'Currency is required'
        })
      }
      if (
        data.useDefaultPaymentTerms === 'custom' &&
        (data.paymentTermsPeriodInDays === undefined ||
          data.paymentTermsPeriodInDays === null)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['paymentTermsPeriodInDays'],
          message: 'Payment period is required'
        })
      }
    })
  type FormValues = z.infer<typeof schema>

  /** Echo the stakeholder's current billing-address fields back unchanged. */
  function unchangedAddressFields(stakeholder: Client | undefined) {
    return {
      billingAddressSameAsPostal:
        stakeholder?.billingAddressSameAsPostal ?? false,
      billingAddress: stakeholder?.billingAddress ?? null
    }
  }

  function buildBillingDetailsPayload(
    value: FormValues
  ): ChangeStakeholderBillingDetailsRequestPayload {
    const isDefaultCurrency = value.useDefaultBillingCurrency === 'default'
    const isDefaultTerms = value.useDefaultPaymentTerms === 'default'
    return {
      ...unchangedAddressFields(_stakeholder.value),
      useDefaultBillingCurrency: isDefaultCurrency,
      billingCurrencyCode: isDefaultCurrency
        ? null
        : value.billingCurrencyCode || null,
      invoiceEmail: value.invoiceEmail || null,
      useDefaultPaymentTerms: isDefaultTerms,
      paymentTermsPeriodInDays: isDefaultTerms
        ? null
        : (value.paymentTermsPeriodInDays ?? null),
      paymentTermsUseRunningMonth: isDefaultTerms
        ? null
        : (value.paymentTermsUseRunningMonth ?? null)
    }
  }

  const form = useForm({
    defaultValues: {
      stakeholderId: '',
      invoiceEmail: '',
      useDefaultBillingCurrency: 'default',
      billingCurrencyCode: '',
      useDefaultPaymentTerms: 'default',
      paymentTermsPeriodInDays: undefined,
      paymentTermsUseRunningMonth: false
    } as FormValues,
    validators: {
      onChange: schema
    },
    onSubmit: async ({ value }) => {
      const payload = buildBillingDetailsPayload(value)
      await service?.commands.changeBillingDetails(value.stakeholderId, payload)
      isOpen.value = false
    }
  })

  /** Map a backend use-default flag (absent = default) onto the radio value. */
  const toDefaultOrCustom = (useDefault: boolean | undefined | null) =>
    (useDefault ?? true) ? 'default' : 'custom'

  function seedBillingDetailsForm(stakeholder: Client) {
    form.reset({
      stakeholderId: stakeholder.id,
      invoiceEmail: stakeholder.invoiceEmail ?? '',
      useDefaultBillingCurrency: toDefaultOrCustom(
        stakeholder.useDefaultBillingCurrency
      ),
      billingCurrencyCode: stakeholder.billingCurrency?.code ?? '',
      useDefaultPaymentTerms: toDefaultOrCustom(
        stakeholder.useDefaultPaymentTerms
      ),
      paymentTermsPeriodInDays:
        stakeholder.paymentTermsPeriodInDays ?? undefined,
      paymentTermsUseRunningMonth:
        stakeholder.paymentTermsUseRunningMonth ?? false
    })
  }

  function showModal({
    stakeholder
  }: ShowStakeholderUpdateBillingDetailsParams) {
    _stakeholder.value = stakeholder
    modalTitle.value = text.groups.billingDetails(
      stakeholder.stakeholderType
    ).modals.updateBillingDetails.title
    modalDescription.value = text.groups
      .billingDetails(stakeholder.stakeholderType)
      .modals.updateBillingDetails.description(stakeholder.name ?? '')
    seedBillingDetailsForm(stakeholder)
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="modalDescription">
        {{ modalDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent class="flex flex-col gap-5">
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="invoiceEmail" label="Invoice Email">
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                placeholder="Email Address of the invoice recipient"
                :data-testid="
                  testIds.stakeholders.forms.billingDetails.invoiceEmailInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>

      <ModalSection>
        <FormField
          :form="form"
          name="useDefaultBillingCurrency"
          label="Invoice Currency"
          :input-group="false"
          required
        >
          <template #default="{ field: currencyField }">
            <RadioCardGroup
              :model-value="currencyField.state.value"
              @update:model-value="currencyField.handleChange"
            >
              <RadioCardGroupItem
                value="default"
                label="Default Invoice Currency"
                description="From Agency Settings"
                :icon="IconCopy"
                :data-testid="
                  testIds.stakeholders.forms.billingDetails
                    .defaultInvoiceCurrencyRadio
                "
              />
              <RadioCardGroupItem
                value="custom"
                label="Custom Invoice Currency"
                description="Manually set the invoice currency"
                :icon="IconEdit"
                :data-testid="
                  testIds.stakeholders.forms.billingDetails
                    .customInvoiceCurrencyRadio
                "
              >
                <template #footer="{ selected }">
                  <FieldGroup v-if="selected">
                    <FormField
                      :form="form"
                      name="billingCurrencyCode"
                      label="Currency"
                      required
                    >
                      <template #default="{ field, invalid }">
                        <CurrencyPicker
                          :id="field.name"
                          :model-value="field.state.value"
                          :aria-invalid="invalid"
                          :data-testid="
                            testIds.stakeholders.forms.billingDetails
                              .invoiceCurrencyInput
                          "
                          @update:model-value="field.handleChange"
                          @blur="field.handleBlur"
                        />
                      </template>
                    </FormField>
                  </FieldGroup>
                </template>
              </RadioCardGroupItem>
            </RadioCardGroup>
          </template>
        </FormField>
      </ModalSection>

      <ModalSection>
        <FormField
          :form="form"
          name="useDefaultPaymentTerms"
          label="Payment Terms"
          :input-group="false"
          required
        >
          <template #default="{ field: paymentTermsField }">
            <RadioCardGroup
              :model-value="paymentTermsField.state.value"
              @update:model-value="paymentTermsField.handleChange"
            >
              <RadioCardGroupItem
                value="default"
                label="Default Payment Terms"
                description="From Agency Settings"
                :icon="IconCopy"
                :data-testid="
                  testIds.stakeholders.forms.billingDetails
                    .defaultPaymentTermsRadio
                "
              />
              <RadioCardGroupItem
                value="custom"
                label="Custom Payment Terms"
                description="Manually set the payment terms"
                :icon="IconEdit"
                :data-testid="
                  testIds.stakeholders.forms.billingDetails
                    .customPaymentTermsRadio
                "
              >
                <template #footer="{ selected }">
                  <FieldGroup v-if="selected">
                    <FormField
                      :form="form"
                      name="paymentTermsPeriodInDays"
                      :label="billingText.inputs.paymentPeriod.label"
                      :hint="billingText.inputs.paymentPeriod.tooltipHint"
                      required
                    >
                      <template #default="{ field }">
                        <InputGroupNumber
                          :id="field.name"
                          :model-value="field.state.value"
                          :placeholder="
                            billingText.inputs.paymentPeriod.placeholder
                          "
                          :data-testid="
                            testIds.stakeholders.forms.billingDetails
                              .creditDaysInput
                          "
                          @update:model-value="field.handleChange"
                          @blur="field.handleBlur"
                        />
                      </template>
                    </FormField>
                    <component
                      :is="form.Field"
                      name="paymentTermsUseRunningMonth"
                    >
                      <template #default="{ field }">
                        <label
                          class="group/field flex cursor-pointer items-start gap-2 px-3 py-2"
                        >
                          <Checkbox
                            :model-value="!!field.state.value"
                            :data-testid="
                              testIds.stakeholders.forms.billingDetails
                                .useCreditRunningMonthCheckbox
                            "
                            @update:model-value="
                              (v) => {
                                field.handleChange(v === true)
                                field.handleBlur()
                              }
                            "
                          />
                          <span class="flex flex-col gap-1">
                            <span
                              class="text-xs font-medium leading-4 text-neutral-700 dark:text-neutral-200"
                            >
                              {{ billingText.inputs.useEndOfMonth.label }}
                            </span>
                            <span
                              v-if="
                                billingText.inputs.useEndOfMonth.description
                              "
                              class="text-muted-foreground whitespace-pre-line text-xs leading-snug"
                            >
                              {{ billingText.inputs.useEndOfMonth.description }}
                            </span>
                          </span>
                        </label>
                      </template>
                    </component>
                  </FieldGroup>
                </template>
              </RadioCardGroupItem>
            </RadioCardGroup>
          </template>
        </FormField>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
